import { Label, Popover, PopoverContent, PopoverTrigger, TextArea } from '@civilgrid/ui/components';
import classNames from 'classnames';
import type React from 'react';
import { useEffect, useId, useRef, useState } from 'react';
import { Notify } from '~/components2/Notify';
import { useIsMounted } from '~/hooks/useIsMounted';

const MIN_HEIGHT = 25; // in pixels
const DEFAULT_TEST_ID = 'free-form-text-cell';

export interface FreeFormTextCellProps {
  initialValue?: string;
  onCommit?: (value: string) => Promise<void>;
  className?: string;
  'data-testid'?: string;
  /** data-testid for the textarea; defaults to `${'data-testid'}-input`. */
  inputTestId?: string;
  /** Visible label of the editor, also used in the cell's accessible name. */
  label?: string;
}

/** Table cell that previews a multiline note on one line and edits it in a popover that saves on close. */
export default function FreeFormTextCell({
  initialValue = '',
  onCommit,
  'data-testid': dataTestId,
  inputTestId,
  label = 'Notes',
}: FreeFormTextCellProps) {
  const testId = dataTestId ?? DEFAULT_TEST_ID;
  const textareaId = useId();
  const [savedValue, setSavedValue] = useState(initialValue);
  const [draft, setDraft] = useState(initialValue);
  const [isOpen, setIsOpen] = useState(false);
  // Radix can report a second dismiss while the popover unmounts; this lets onOpenChange ignore it before re-render.
  const isOpenRef = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Radix skips focus return once the user has clicked outside, so keyboard closes restore it themselves.
  const closedByKeyboardRef = useRef(false);
  const isMounted = useIsMounted();

  useEffect(() => {
    setSavedValue(initialValue);
  }, [initialValue]);

  const preview = savedValue.replace(/\s*\n\s*/g, ' ').trim();

  function setOpen(next: boolean) {
    isOpenRef.current = next;
    setIsOpen(next);
  }

  function open() {
    closedByKeyboardRef.current = false;
    setDraft(savedValue);
    setSaveFailed(false);
    setOpen(true);
  }

  // Click outside, Escape and Ctrl/Cmd+Enter save; a failed save keeps the popover open with the draft.
  async function closeAndSave() {
    if (isSaving) return;
    if (draft === savedValue) {
      setOpen(false);
      return;
    }
    setIsSaving(true);
    setSaveFailed(false);
    try {
      await onCommit?.(draft);
      if (!isMounted()) return;
      setSavedValue(draft);
      setOpen(false);
    } catch {
      Notify.error({ message: 'An error occurred while saving the notes.' });
      if (isMounted()) setSaveFailed(true);
    } finally {
      if (isMounted()) setIsSaving(false);
    }
  }

  // After a failed save, Escape drops the draft so the user isn't stuck retrying; click outside still retries.
  function onEscapeKeyDown(e: KeyboardEvent) {
    closedByKeyboardRef.current = true;
    if (!saveFailed || isSaving) return;
    e.preventDefault();
    setDraft(savedValue);
    setSaveFailed(false);
    setOpen(false);
  }

  function onOpenChange(next: boolean) {
    if (next) open();
    else if (isOpenRef.current) void closeAndSave();
  }

  function onTextareaKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && !e.nativeEvent.isComposing) {
      e.preventDefault();
      closedByKeyboardRef.current = true;
      void closeAndSave();
    }
  }

  const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <div
      style={{
        height: `calc(100% + 7px)`,
        width: `calc(100% + 1rem)`,
        minHeight: MIN_HEIGHT,
      }}
      className="relative -mx-2 -my-1"
      data-testid={testId}
    >
      <Popover open={isOpen} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>
          <button
            ref={triggerRef}
            type="button"
            aria-label={preview ? `${label}: ${preview}` : `Add ${label.toLowerCase()}`}
            className={classNames(
              'block h-full w-full cursor-pointer truncate rounded-none border p-1.5 text-left focus-ring',
              isOpen ? 'border-focus' : 'border-transparent hover:border-grey-50',
            )}
            data-testid={`${testId}-trigger`}
            onClick={stopPropagation}
            style={{ minHeight: MIN_HEIGHT }}
          >
            {preview}
          </button>
        </PopoverTrigger>

        <PopoverContent
          side="right"
          align="start"
          aria-label={label}
          className="flex h-[300px] w-[200px] flex-col gap-2 p-2 -mt-8"
          data-testid={`${testId}-popover`}
          // Clicks in the portalled content still bubble through the React tree to the row.
          onClick={stopPropagation}
          onEscapeKeyDown={onEscapeKeyDown}
          onCloseAutoFocus={(e) => {
            if (!closedByKeyboardRef.current) return;
            e.preventDefault();
            triggerRef.current?.focus();
          }}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            const el = textareaRef.current;
            el?.focus();
            el?.setSelectionRange(el.value.length, el.value.length);
          }}
        >
          <Label htmlFor={textareaId}>{label}</Label>
          <TextArea
            ref={textareaRef}
            id={textareaId}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onTextareaKeyDown}
            readOnly={isSaving}
            aria-busy={isSaving || undefined}
            className="min-h-0 flex-1 resize-none overflow-y-auto whitespace-pre-wrap break-words"
            data-testid={inputTestId ?? `${testId}-input`}
          />

          <output className="block min-h-4 text-xs text-grey-80" data-testid={`${testId}-status`}>
            {isSaving
              ? 'Saving…'
              : saveFailed
                ? 'Couldn’t save. Click outside to retry or press Escape to discard.'
                : ''}
          </output>
        </PopoverContent>
      </Popover>
    </div>
  );
}
